import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cpu } from "lucide-react";
import type { HubDeviceDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const fmt = (v: string | null | undefined) => (v ? new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" }) : "—");
const gb = (mb: number | null | undefined) => (mb == null ? "—" : `${(mb / 1024).toFixed(1)} GB`);
function up(sec: number | null | undefined) {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d} d ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
}
function ago(v: string) {
  const m = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  return m < 1 ? "upravo" : m < 60 ? `pre ${m} min` : m < 1440 ? `pre ${Math.round(m / 60)} h` : `pre ${Math.round(m / 1440)} d`;
}

/** Upozorenja na osnovu poslednjeg stanja. */
function warnings(d: HubDeviceDto): string[] {
  const s = d.status, w: string[] = [];
  if (!d.online) w.push("Ne javlja se (duže od 10 min)");
  if (!s) return w;
  if (s.cpuTempC != null && s.cpuTempC >= 75) w.push(`Visoka temperatura ${s.cpuTempC.toFixed(0)} °C`);
  if (s.throttled) w.push("Procesor usporen (napajanje/temperatura)");
  if (s.diskFreeMb != null && s.diskFreeMb < 500) w.push("Skoro pun disk");
  if (s.ramFreeMb != null && s.ramFreeMb < 100) w.push("Malo slobodne memorije");
  if (s.cardPresent === false) w.push("Kartica nije u čitaču");
  if (s.taxCoreLastError) w.push("Greška Poreske uprave");
  if (s.pendingQueue != null && s.pendingQueue > 0) w.push(`Na čekanju: ${s.pendingQueue}`);
  if (d.paket && d.paket.activeUntil && new Date(d.paket.activeUntil) < new Date()) w.push("Paket istekao");
  if (!d.paket && d.jid) w.push("JID nije vezan za paket");
  return w;
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <p className="text-sm"><span className="text-slate-500">{k}: </span><span className="text-slate-800">{v}</span></p>;
}

function Card({ d, onChange }: { d: HubDeviceDto; onChange: () => void }) {
  const s = d.status;
  const w = warnings(d);
  const dis = useMutation({ mutationFn: () => api.adminSetHubDisabled(d.id, !d.disabled), onSuccess: onChange });
  const del = useMutation({ mutationFn: () => api.adminDeleteHub(d.id), onSuccess: onChange });
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{d.name || d.serial}</p>
          <p className="text-xs text-slate-500">{d.companyName} · PIB {d.taxId} · serijski {d.serial}</p>
        </div>
        <div className="flex items-center gap-2">
          {d.disabled && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">isključen</span>}
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${d.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {d.online ? "Na mreži" : "Van mreže"} · {ago(d.lastSeenAt)}
          </span>
        </div>
      </div>
      {w.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {w.map((x) => <span key={x} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">{x}</span>)}
        </div>
      )}
      <div className="mt-3 grid gap-4 md:grid-cols-4">
        <div>
          <p className="text-xs uppercase text-slate-400">Uređaj</p>
          <Row k="Model" v={d.hwModel ?? "—"} />
          <Row k="Sistem" v={d.osVersion ?? "—"} />
          <Row k="Hub" v={d.hubVersion ?? "—"} />
          <Row k="HA-LPFR" v={d.lpfrVersion ?? "—"} />
          <Row k="Prvi put viđen" v={fmt(d.firstSeenAt)} />
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">Zdravlje</p>
          <Row k="Temperatura" v={s?.cpuTempC != null ? `${s.cpuTempC.toFixed(0)} °C` : "—"} />
          <Row k="Opterećenje" v={s?.load1 ?? "—"} />
          <Row k="Memorija slobodno" v={`${gb(s?.ramFreeMb)} / ${gb(s?.ramTotalMb)}`} />
          <Row k="Disk slobodno" v={`${gb(s?.diskFreeMb)} / ${gb(s?.diskTotalMb)}`} />
          <Row k="Radi bez restarta" v={up(s?.uptimeSeconds)} />
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">Mreža i kartica</p>
          <Row k="IP adresa" v={d.ip ?? "—"} />
          <Row k="Veza" v={s?.network ? `${s.network}${s.wifiSsid ? ` (${s.wifiSsid}${s.signalDbm != null ? `, ${s.signalDbm} dBm` : ""})` : ""}` : "—"} />
          <Row k="Čitač" v={s?.cardReader ?? "—"} />
          <Row k="Kartica" v={s?.cardPresent == null ? "—" : s.cardPresent ? "u čitaču" : "nema"} />
          <Row k="JID" v={d.jid ?? "—"} />
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">Fiskalizacija</p>
          <Row k="Računa danas" v={s?.signedToday ?? "—"} />
          <Row k="Ukupno" v={s?.signedTotal ?? "—"} />
          <Row k="Poslednji račun" v={fmt(s?.lastInvoiceAt)} />
          <Row k="Poreska uprava" v={fmt(s?.taxCoreLastContact)} />
          <Row k="Paket" v={d.paket ? `${d.paket.label ?? "paket"} (${d.paket.kind === "HORECA" ? "HoReCa" : "obična"})${d.paket.location ? ` · ${d.paket.location}` : ""}` : "—"} />
        </div>
      </div>
      {(s?.taxCoreLastError || s?.lastError) && (
        <p className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">{s.taxCoreLastError ?? s.lastError}</p>
      )}
      <div className="mt-3 flex gap-2">
        <Button variant="secondary" onClick={() => dis.mutate()} disabled={dis.isPending}>{d.disabled ? "Uključi" : "Isključi"}</Button>
        <Button variant="secondary" onClick={() => { if (window.confirm("Obrisati uređaj sa spiska? Pojaviće se ponovo kad se javi.")) del.mutate(); }}>Ukloni sa spiska</Button>
      </div>
    </div>
  );
}

export function HubAdminPage() {
  const qc = useQueryClient();
  const [only, setOnly] = useState<"all" | "problem">("all");
  const q = useQuery({ queryKey: ["hub-admin"], queryFn: () => api.adminListHub(), refetchInterval: 60000 });
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) return <EmptyState icon={Cpu} title="Greška" description="Pregled uređaja nije dostupan." />;
  const all = q.data;
  const list = only === "problem" ? all.filter((d) => warnings(d).length > 0) : all;
  const online = all.filter((d) => d.online).length;
  const refresh = () => qc.invalidateQueries({ queryKey: ["hub-admin"] });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">HA Fiscal Hub</h1>
        <div className="flex gap-2">
          <Button variant={only === "problem" ? "primary" : "secondary"} onClick={() => setOnly(only === "all" ? "problem" : "all")}>Samo sa upozorenjem</Button>
          <Button variant="secondary" onClick={refresh}>Osveži</Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        {[["Uređaja", all.length], ["Na mreži", online], ["Van mreže", all.length - online], ["Sa upozorenjem", all.filter((d) => warnings(d).length > 0).length]].map(([l, v]) => (
          <div key={String(l)} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
            <p className="text-xs text-slate-500">{l}</p>
            <p className="text-xl font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>
      {list.length === 0 ? (
        <EmptyState icon={Cpu} title="Nema uređaja" description="Hub se pojavljuje ovde čim se prvi put javi sa važećom L-PFR licencom." />
      ) : (
        <div className="space-y-3">{list.map((d) => <Card key={d.id} d={d} onChange={refresh} />)}</div>
      )}
    </div>
  );
}
